import { useMemo } from "react";

import { usePermissions } from "@/hooks/usePermissions";
import type { IWidgetDefinition } from "./types";

/**
 * Resolves a widget catalogue against the caller's permissions, once, into
 * values everything downstream can memoise on.
 *
 * `usePermissions` hands back a `can()` closure that is new on every render, so
 * it can never be a dependency of anything. Every consumer that needed a
 * permission answer used to re-derive it and re-allocate. Here it is folded once
 * into a fingerprint string, and what leaves this hook is a `Set` and two arrays
 * whose identities only change when the permission set genuinely does.
 */
export function useWidgetRegistry(registry: IWidgetDefinition[]) {
  const { can, isLoading } = usePermissions();

  const definitions = useMemo(
    () => new Map(registry.map((definition) => [definition.type, definition])),
    [registry],
  );

  /**
   * One character per catalogue entry. Cheap for a catalogue this size, and it
   * is the only honest memo key available while `can` itself is unstable.
   */
  const permissionFingerprint = registry
    .map((definition) =>
      can(definition.section, definition.action ?? "view") ? "1" : "0",
    )
    .join("");

  const allowedTypes = useMemo(
    () =>
      new Set(
        registry
          .filter((_, index) => permissionFingerprint[index] === "1")
          .map((definition) => definition.type),
      ),
    [registry, permissionFingerprint],
  );

  /** Catalogue entries the caller may see, in registry order. */
  const availableWidgets = useMemo(
    () => registry.filter((definition) => allowedTypes.has(definition.type)),
    [registry, allowedTypes],
  );

  return { definitions, allowedTypes, availableWidgets, isLoading };
}
